import type { EventProps } from 'react-big-calendar';
import { useMemo } from 'react';
import { ThemeConstellation } from '@zillow/constellation';
import { useMedia } from 'react-use';

import { StyledFullHeightDiv, StyledNoWrapParagraph } from './StyledComponents';
import { getTourEventTimeRange } from '../../../../utils/formatTime';
import { type CalendarEventHandlers, type ZCalendarEvent } from '../../../../constants/TourCalendar';
import CalendarEventPopper from '../CalendarEventPopper';
import CalendarEventPopover from './components/CalendarEventPopover';
import { getIsAvailabilityEvent } from '../../../../utils/calendarEventGetters';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';

const { breakpoints } = ThemeConstellation.ns();

type CalendarEventWrapperParams = {
  canRescheduleTours: boolean;
  selectedEvent?: ZCalendarEvent;
  displayEventEditPopover: boolean;
  handlers: CalendarEventHandlers;
  tourDuration: TourDuration;
};
type CalendarEventProps = CalendarEventWrapperParams & EventProps<ZCalendarEvent>;

const CalendarEvent = (props: CalendarEventProps) => {
  const { canRescheduleTours, event, title, selectedEvent, displayEventEditPopover, handlers, tourDuration } = props;

  const isScreenLarge = useMedia(`(min-width: ${breakpoints.lg}px)`);
  const timeRange = useMemo(() => getTourEventTimeRange(event), [event]);

  const isCalendarEventPopperOpen =
    event.id === selectedEvent?.id && !getIsAvailabilityEvent(event) && !canRescheduleTours;

  return (
    <CalendarEventPopper
      event={event}
      popoverBody={
        <CalendarEventPopover
          event={event}
          displayEventEditPopover={displayEventEditPopover}
          handlers={handlers}
          tourDuration={tourDuration}
        />
      }
      onClose={handlers.onClose}
      modal={!isScreenLarge}
      isOpen={isCalendarEventPopperOpen}
    >
      <StyledFullHeightDiv title={`${title} ${timeRange}`}>
        <StyledNoWrapParagraph className="calendar-event-text" fontType="bodySmallHeading">
          {title}
        </StyledNoWrapParagraph>
        <StyledNoWrapParagraph className="calendar-event-text" fontType="finePrint">
          {timeRange}
        </StyledNoWrapParagraph>
      </StyledFullHeightDiv>
    </CalendarEventPopper>
  );
};

const CalendarEventWrapper = (params: CalendarEventWrapperParams) => (props: EventProps<ZCalendarEvent>) => {
  return <CalendarEvent {...params} {...props} />;
};

export default CalendarEventWrapper;
